import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { CheckCircle2, Circle, Star, Trophy, Flame } from 'lucide-react';
import { toast } from 'sonner';

export default function DailyTasks({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingTask, setCompletingTask] = useState(null);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const res = await axios.get(`${API}/daily-tasks?date=${today}`);
      setTasks(res.data);
    } catch (error) {
      toast.error('載入任務失敗');
    } finally {
      setLoading(false);
    }
  };

  const completeTask = async (taskId) => {
    setCompletingTask(taskId);
    try {
      await axios.post(`${API}/daily-tasks/${taskId}/complete`);
      toast.success('🎉 任務完成！獲得1積分', {
        duration: 3000,
        className: 'bg-gradient-to-r from-green-400 to-green-600'
      });
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || '完成任務失敗');
    } finally {
      setCompletingTask(null);
    }
  };

  const taskIcons = {
    physical: { icon: '💪', color: 'from-red-500 to-orange-500', label: '體能訓練' },
    stretching: { icon: '🧘', color: 'from-purple-500 to-pink-500', label: '柔軟度' },
    jumping: { icon: '🤸', color: 'from-blue-500 to-cyan-500', label: '跳躍訓練' }
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const totalCount = tasks.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="daily-tasks-page">
      <div className="max-w-5xl mx-auto">
        {/* Header with progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                每日任務
                {completedCount === totalCount && totalCount > 0 && (
                  <span className="animate-bounce">
                    <Trophy className="text-yellow-500" size={36} />
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-2">完成每日任務，獲得積分！</p>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                {completedCount}/{totalCount}
              </div>
              <p className="text-sm text-gray-600 mt-1">任務完成</p>
            </div>
          </div>

          {/* Animated Progress Bar */}
          <div className="relative">
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 rounded-full transition-all duration-1000 ease-out relative"
                style={{ width: `${progressPercentage}%` }}
              >
                {progressPercentage > 0 && (
                  <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">開始</span>
              <span className="text-xs font-medium text-gray-700">{Math.round(progressPercentage)}%</span>
              <span className="text-xs text-gray-500">完成</span>
            </div>
          </div>

          {/* Streak indicator */}
          {completedCount > 0 && (
            <div className="mt-4 flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-orange-50 to-pink-50 rounded-lg border border-orange-200">
              <Flame className="text-orange-500" size={20} />
              <span className="text-sm font-medium text-gray-700">
                今日已完成 <span className="text-orange-600 font-bold">{completedCount}</span> 個任務！
              </span>
              <Star className="text-yellow-500" size={20} />
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-500"></div>
            <p className="mt-4 text-gray-600">載入中...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">😴</div>
            <p className="text-gray-500 text-lg">今日暫無任務，請稍後再來</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tasks.map((task, index) => {
              const taskType = taskIcons[task.task_type] || { icon: '⭐', color: 'from-gray-500 to-gray-600', label: '任務' };
              const isCompleting = completingTask === task.task_id;
              
              return (
                <div 
                  key={task.task_id} 
                  className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden ${
                    task.completed ? 'ring-2 ring-green-500' : ''
                  }`}
                  data-testid="task-item"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInUp 0.6s ease-out forwards'
                  }}
                >
                  {/* Task Header with Gradient */}
                  <div className={`bg-gradient-to-br ${taskType.color} p-6 text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                    
                    <div className="relative z-10">
                      <div className="text-6xl mb-3 filter drop-shadow-lg">{taskType.icon}</div>
                      <div className="text-xs font-medium opacity-90">{taskType.label}</div>
                    </div>
                  </div>

                  {/* Task Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{task.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 min-h-[40px]">{task.description}</p>
                    
                    {/* Points Badge */}
                    <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-gradient-to-r from-orange-50 to-pink-50 rounded-lg border border-orange-200">
                      <Star className="text-yellow-500 fill-yellow-500" size={24} />
                      <span className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                        +{task.points}
                      </span>
                      <span className="text-gray-600 text-sm font-medium">積分</span>
                    </div>

                    {/* Complete Button or Status */}
                    {task.completed ? (
                      <div className="flex items-center justify-center gap-2 text-green-600 py-3 bg-green-50 rounded-lg">
                        <CheckCircle2 size={24} className="animate-bounce" />
                        <span className="font-semibold">已完成</span>
                      </div>
                    ) : (
                      <Button
                        data-testid={`complete-task-btn-${task.task_id}`}
                        onClick={() => completeTask(task.task_id)}
                        disabled={isCompleting}
                        className={`w-full bg-gradient-to-r ${taskType.color} hover:opacity-90 transition-all duration-300 transform active:scale-95 py-6 text-lg font-semibold shadow-lg`}
                      >
                        {isCompleting ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                            <span>完成中...</span>
                          </div>
                        ) : (
                          '完成任務'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Achievement Banner */}
        {completedCount === totalCount && totalCount > 0 && (
          <div className="mt-8 p-6 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl shadow-2xl text-white text-center animate-bounce">
            <Trophy size={48} className="mx-auto mb-3" />
            <h2 className="text-2xl font-bold mb-2">🎉 恭喜完成所有任務！</h2>
            <p className="text-lg">你今天真棒！繼續保持，明天再來挑戰吧！</p>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
